/**
 * SnoozeDialog
 *
 * Dialog for choosing snooze time with presets and custom date/time picker.
 */

import { useState } from 'react';
import { Clock, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SnoozeDialogProps {
  open: boolean;
  onClose: () => void;
  onSnooze: (snoozeUntil: string) => void;
}

function getPresets(): { label: string; value: string }[] {
  const now = new Date();

  const laterToday = new Date(now);
  laterToday.setHours(laterToday.getHours() + 3);

  const tomorrowMorning = new Date(now);
  tomorrowMorning.setDate(tomorrowMorning.getDate() + 1);
  tomorrowMorning.setHours(9, 0, 0, 0);

  const tomorrowAfternoon = new Date(now);
  tomorrowAfternoon.setDate(tomorrowAfternoon.getDate() + 1);
  tomorrowAfternoon.setHours(14, 0, 0, 0);

  const thisWeekend = new Date(now);
  const daysToSat = (6 - thisWeekend.getDay() + 7) % 7 || 7;
  thisWeekend.setDate(thisWeekend.getDate() + daysToSat);
  thisWeekend.setHours(9, 0, 0, 0);

  const nextMonday = new Date(now);
  const daysToMon = (8 - nextMonday.getDay()) % 7 || 7;
  nextMonday.setDate(nextMonday.getDate() + daysToMon);
  nextMonday.setHours(9, 0, 0, 0);

  return [
    { label: 'Later today', value: laterToday.toISOString() },
    { label: 'Tomorrow morning', value: tomorrowMorning.toISOString() },
    { label: 'Tomorrow afternoon', value: tomorrowAfternoon.toISOString() },
    { label: 'This weekend', value: thisWeekend.toISOString() },
    { label: 'Next week', value: nextMonday.toISOString() },
  ];
}

export function SnoozeDialog({ open, onClose, onSnooze }: SnoozeDialogProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');
  const presets = getPresets();

  const handleCustomSnooze = () => {
    if (!customDate) return;
    const dt = new Date(`${customDate}T${customTime}`);
    onSnooze(dt.toISOString());
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4" /> Snooze until
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1">
          {presets.map((p) => (
            <Button
              key={p.label}
              variant="ghost"
              className="w-full justify-between text-sm h-9"
              onClick={() => {
                onSnooze(p.value);
                onClose();
              }}
            >
              <span>{p.label}</span>
              <span className="text-xs text-muted-foreground">
                {new Date(p.value).toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </Button>
          ))}

          <div className="border-t border-border pt-2 mt-2">
            {showCustom ? (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Time</Label>
                  <Input
                    type="time"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <Button
                  size="sm"
                  className="w-full"
                  onClick={handleCustomSnooze}
                  disabled={!customDate}
                >
                  Snooze
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                className="w-full justify-start text-sm h-9 gap-2"
                onClick={() => setShowCustom(true)}
              >
                <Calendar className="h-4 w-4" /> Pick date & time
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
